// @givmo/ai — the assistant markdown renderer. Renders a `text` block's markdown
// via Streamdown (^2.5) with **images disabled** and **NO model-authored URLs
// rendered as links** (trust posture §3.2 — navigation is exclusively via
// validated `action_chips`, never model text).
//
// Streamdown 2.5 exposes the underlying react-markdown `Options` (the `security`
// prop was removed in the 2.x line). We disable images + links the robust way:
//   - `disallowedElements={['img', 'a']}` strips both at the AST level;
//   - `unwrapDisallowed` keeps the inner text (alt text / link label) as plain
//     prose so content is never silently dropped;
//   - explicit no-op `img`/`a` component overrides are belt-and-suspenders so even
//     a future raw-HTML path can't produce a navigable element;
//   - a `urlTransform` that nulls every URL is the last line of defense.
// The result: model text renders bold/italic/lists/code as prose; an `![](…)`
// renders nothing (its alt text stays as text); a `[label](url)` renders the bare
// `label` with no href.
import type { ReactElement } from 'react';
import { Streamdown, type Components } from 'streamdown';

export interface AssistantMarkdownTextProps {
  /** The markdown source (a `text` block's `text`, or a degrade fallback string). */
  text: string;
  /** Optional className forwarded to Streamdown's root. */
  className?: string;
  /** A stable `data-testid` on the root (the design-system convention). */
  testId?: string;
}

/** No-op renderers: images render nothing; "links" render their bare label text
 * with no anchor, so a model-authored URL is never navigable. */
const SAFE_COMPONENTS: Components = {
  img: () => null,
  a: ({ children }) => <>{children}</>,
};

/** Null every URL — defense in depth behind `disallowedElements`. */
function blockAllUrls(): string {
  return '';
}

export function AssistantMarkdownText({
  text,
  className,
  testId,
}: AssistantMarkdownTextProps): ReactElement {
  return (
    <div data-testid={testId} className={className}>
      <Streamdown
        mode="static"
        disallowedElements={['img', 'a']}
        unwrapDisallowed
        urlTransform={blockAllUrls}
        components={SAFE_COMPONENTS}
        // No remote/data images, no link navigation — the trust posture.
        linkSafety={{ enabled: true, onLinkCheck: () => false }}
      >
        {text}
      </Streamdown>
    </div>
  );
}
